import {Component, inject} from '@angular/core';
import {VendorModel} from '../../service/vendor.service';
import {VendorViewerModalComponent,VendorViewerType} from '../viewer/vendor-viewer-modal/vendor-viewer-modal.component';
import {AutoUnsubscribe} from '../../../../infrastructure/decorators/auto-unsubscribe.decorator';
import {VendorTemplate} from '../../model/vendor-template';
import {MatDialog} from '@angular/material/dialog';
import {GridEventOutput, GridModel} from 'src/app/shared/model/page-grid';
import {SearchVendorResponse} from '../../model/search-vendor-response';
import {VendorGridService} from '../../service/vendor-grid.service';
import {TramsDataSourceModel} from 'src/app/payments/models/trams-data-source';
import {TramsDataSourceService} from 'src/app/payments/service/trams-data-source.service';

@AutoUnsubscribe()
@Component({
    selector: 'vendor-list',
    templateUrl: './vendor-list.component.html',
    standalone: false
})
export class VendorListComponent {
  private dialog = inject(MatDialog);
  private tramsDataSourceService = inject(TramsDataSourceService);

  protected vendorViewerType = VendorViewerType;
  protected readonly vendorsGrid: GridModel<SearchVendorResponse> = {
    gridDataTestId: 'vendors-list',
    gridTitle: 'Vendors',
    replayButton: true,
    singleColumnFilter: true,
    columns: [
      {
        columnProperty: 'id',
        columnHeader: 'FMS Vendor Number',
        sort: true,
        clickable: true,
      },
      {
        columnProperty: 'vendorName',
        columnHeader: 'Vendor Name',
        sort: true,
        textLeft: true,
      },
      {
        columnProperty: 'Address.Country.Name',
        columnHeader: 'Country',
        columnPropertyFunction: (vendor: VendorModel)=> vendor.vendorCountryName || '',
        sort: true,
      },
      {
        columnProperty: 'Address.City',
        columnHeader: 'City',
        columnPropertyFunction: (vendor: VendorModel)=> vendor.vendorCity || '',
        sort: true,
      },
      {
        columnProperty: 'vendorPaymentTC',
        columnHeader: 'Vendor Payment TC',
        sort: false,
        columnIconDisplay: 'checkmark',
        shouldHideCellContent: (vendor: VendorModel) => this.checkVendorTemplate(vendor, VendorViewerType.VendorPayment),
      },
      {
        columnProperty: 'cancellationRefundTC',
        columnHeader: 'Cancellation Refund TC',
        sort: false,
        columnIconDisplay: 'checkmark',
        shouldHideCellContent: (vendor:VendorModel) => this.checkVendorTemplate(vendor, VendorViewerType.CancellationRefund)
      },
      {
        columnProperty: 'tramsDataSource.sourceName',
        columnHeader: 'Data Source',
        columnPropertyFunction: (vendor: VendorModel)=> vendor.dataSourceName ?? '',
        sort: true,
      },
    ],
    filters: [
      {
        filterProperty: 'vendorName',
        filterDisplayName: 'Vendor Name',
        filterType: 'AUTOCOMPLETE',
        config: {
          autocompleteTableName: 'Vendor',
          autocompleteColumnName: 'VendorName',
          autocompleteEmitText: true,
        }
      },
      {
        filterProperty: 'vendorCity',
        filterDisplayName: 'Vendor City',
        filterType: 'AUTOCOMPLETE',
        config: {
          autocompleteTableName: 'Address',
          autocompleteColumnName: 'City',
          autocompleteEmitText: true,
        }
      },
      {
        filterProperty: 'vendorCountry',
        filterDisplayName: 'Vendor Country',
        filterType: 'AUTOCOMPLETE',
        config: {
          autocompleteTableName: 'Country',
          autocompleteColumnName: 'Name',
          autocompleteEmitText: true,
        }
      },
      {
        filterProperty: 'dataSource',
        filterDisplayName: 'Data Source',
        filterType: 'DROPDOWN',
        config: {
          dropdownOptions: this.tramsDataSourceService.getTramsDataSourceList(),
          dropdownValueProp: 'id',
          dropdownDisplayWith: (item: TramsDataSourceModel) => item.sourceName
        }
      },
    ],
    gridGetService: inject(VendorGridService)
  };

  protected openDialog({item, column}: GridEventOutput<VendorModel>) {
    if(column === 'cancellationRefundTC') {
      this.openModal(item, this.vendorViewerType.CancellationRefund);
      return;
    }

    if(column === 'vendorPaymentTC') {
      this.openModal(item, this.vendorViewerType.VendorPayment);
      return;
    }

    this.openModal(item, this.vendorViewerType.Vendor);
  }

  private openModal(record: VendorModel, viewerType: VendorViewerType): void {
    let titleHeader;

    if (viewerType == VendorViewerType.VendorPayment) {
      titleHeader = 'Vendor Payment Terms and Conditions';
    } else if (viewerType == VendorViewerType.CancellationRefund) {
      titleHeader = 'Cancellation Refund Terms and Conditions';
    } else {
      titleHeader = 'Vendor Information';
    }

    const vendorViewerRef = this.dialog.open(VendorViewerModalComponent, { width: '55%' });
    const vendorViewerInstance = vendorViewerRef.componentInstance as VendorViewerModalComponent;
    vendorViewerInstance.title = titleHeader;
    vendorViewerInstance.viewerType = viewerType;
    vendorViewerInstance.vendorData = record;
  }

  private checkVendorTemplate(vendor:VendorModel, viewer: VendorViewerType.CancellationRefund | VendorViewerType.VendorPayment ): boolean {
    const vendorTemplates: VendorTemplate[] = vendor?.vendorTemplates;
    return vendorTemplates?.some(x => !x.isDeleted && x.termsConditionsTypeId == Number(viewer)); 
  }
}
